import {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { paths } from "utils/paths";
import { api } from "api";
import { useTranslation } from "react-i18next";
import { toaster } from "utils/toaster";
import { useRoleManager } from "./RoleManagerProvider";
import { useQuery } from "@tanstack/react-query";

const sortCropsAndAreas = (arr) => {
  const arrSorted = arr
    .map((crop) => {
      const childrenSorted = crop.children.sort((a, b) =>
        a.label.localeCompare(b.label)
      );
      return { ...crop, children: childrenSorted };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  return arrSorted;
};

const findAllCropsOfFields = (fields, isEn) => {
  const crops = fields.reduce((acc, field) => {
    const cropId = field.varietyRead?.cropId;
    if (!cropId) return acc;
    const varietyId = field.varietyRead.varietyId;
    const varietyName = isEn
      ? field.varietyRead.varietyNameEn
      : field.varietyRead.varietyNameGr;

    const existingCrop = acc.find((crop) => crop.value === cropId);
    if (existingCrop) {
      const existingVariety = existingCrop.children.find(
        (variety) => variety.value === varietyId
      );

      if (existingVariety) {
        return acc;
      } else {
        const variety = {
          value: varietyId,
          label: varietyName,
          parentIdPath: [cropId],
        };
        existingCrop.children.push(variety);
        return acc;
      }
    } else {
      const cropName = isEn
        ? field.varietyRead.cropNameEn
        : field.varietyRead.cropNameGr;

      const crop = {
        value: cropId,
        label: cropName,
        children: [
          {
            value: varietyId,
            label: varietyName,
            parentIdPath: [cropId],
          },
        ],
        parentIdPath: [],
      };
      return [...acc, crop];
    }
  }, []);

  const cropsSorted = sortCropsAndAreas(crops);

  return cropsSorted;
};

const findAllAreasOfFields = (fields, isEn) => {
  const areas = fields.reduce((acc, field) => {
    if (!field.municipality) return acc;

    const areaInfo = field.municipality;

    const regionId = areaInfo.regionId;
    const prefectureId = areaInfo.prefectureId;
    const prefectureNameGr = areaInfo.prefectureNameGr;
    const prefectureNameEn = areaInfo.prefectureNameEn;
    const municipalityId = areaInfo.id;
    const municipalityNameGr = areaInfo.nameGr;
    const municipalityNameEn = areaInfo.nameEn;

    const existingRegion = acc.find((area) => area.value === regionId);
    if (existingRegion) {
      const existingPrefecture = existingRegion.children.find(
        (prefecture) => prefecture.value === prefectureId
      );
      if (existingPrefecture) {
        const existingMunicipality = existingPrefecture.children.find(
          (municipality) => municipality.value === municipalityId
        );
        if (existingMunicipality) {
          return acc;
        } else {
          const municipality = {
            value: municipalityId,
            label: isEn ? municipalityNameEn : municipalityNameGr,
            parentIdPath: [regionId, prefectureId],
          };
          existingPrefecture.children.push(municipality);
          return acc;
        }
      } else {
        const prefecture = {
          value: prefectureId,
          label: isEn ? prefectureNameEn : prefectureNameGr,
          children: [
            {
              value: municipalityId,
              label: isEn ? municipalityNameEn : municipalityNameGr,
              parentIdPath: [regionId, prefectureId],
            },
          ],
          parentIdPath: [regionId],
        };
        existingRegion.children.push(prefecture);
        return acc;
      }
    } else {
      const regionNameGr = areaInfo.regionNameGr;
      const regionNameEn = areaInfo.regionNameEn;
      const area = {
        value: regionId,
        label: isEn ? regionNameEn : regionNameGr,
        children: [
          {
            value: prefectureId,
            label: isEn ? prefectureNameEn : prefectureNameGr,
            children: [
              {
                value: municipalityId,
                label: isEn ? municipalityNameEn : municipalityNameGr,
                parentIdPath: [regionId, prefectureId],
              },
            ],
            parentIdPath: [regionId],
          },
        ],
        parentIdPath: [],
      };
      return [...acc, area];
    }
  }, []);

  const areasSorted = sortCropsAndAreas(areas);
  return areasSorted;
};

const findAllEcoSchemesOfFields = (fields) => {
  const ecoSchemes = {};
  for (const field of fields) {
    if (!field.ecoschemes) continue;

    for (const scheme of field.ecoschemes) {
      const actualSchemes = scheme.fieldEcoschemeSubsidyList ?? [];
      for (const actualScheme of actualSchemes) {
        const actualSchemeName = actualScheme.ecoschemeSubsidyCode;
        if (!ecoSchemes[actualSchemeName]) {
          ecoSchemes[actualSchemeName] = {
            value: actualSchemeName,
            label: actualSchemeName,
          };
        }
      }
    }
  }
  const ecoSchemesArray = Object.values(ecoSchemes);
  return ecoSchemesArray;
};

const FieldsContext = createContext({
  fields: [],
  fetchState: {
    isLoading: false,
    isSuccess: false,
    isError: false,
    error: undefined,
  },
});

const FieldsContextActions = createContext({
  fetchUserFields: ({ onSuccess, onError }) => {},
  createNewField: ({ postData, onSuccess, onError }) => {},
  getMyCrops: () => {},
  getMyLocations: () => {},
  getMyEcoSchemes: () => {},
  updateField: ({ id, patchData, onSuccess, onError }) => {},
  deleteField: ({ id, onSuccess, onError }) => {},
  cancelCollaboration: ({ id, onSuccess, onError }) => {},
});

const FieldsProvider = ({ children }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  const { currentTeam } = useRoleManager();

  const {
    data: fields = [],
    isPending,
    isError,
    isSuccess,
    error,
    refetch,
  } = useQuery({
    queryKey: ["my-fields", currentTeam?.id],
    queryFn: () => api.fields.fetchMy(currentTeam?.id),
    select: (res) => res?.data?.fields ?? [],
  });

  const fetchState = useMemo(
    () => ({ error, isLoading: isPending, isError, isSuccess }),
    [error, isPending, isError, isSuccess]
  );

  const fetchUserFields = useCallback(
    async (handlers = { onSuccess: null, onError: null }) => {
      const { onSuccess, onError } = handlers;
      try {
        const result = await refetch();
        onSuccess?.(result?.data);
        return result;
      } catch (err) {
        onError?.(err);
        throw err;
      }
    },
    [refetch]
  );

  const getMyCrops = useCallback(() => {
    const crops = findAllCropsOfFields(fields, isEn);
    return crops;
  }, [fields, isEn]);

  const getMyLocations = useCallback(() => {
    const areas = findAllAreasOfFields(fields, isEn);
    return areas;
  }, [fields, isEn]);

  const getMyEcoSchemes = useCallback(() => {
    const ecoSchemes = findAllEcoSchemesOfFields(fields);
    return ecoSchemes?.sort((a, b) => a.label.localeCompare(b.label));
  }, [fields]);

  // Initial fetch is handled by useQuery (enabled by team id)

  const createNewField = useCallback(
    ({ postData, onSuccess, onError }) => {
      api.fields
        .create(postData)
        .then(({ data: field }) => {
          navigate?.(paths.fields.one(field.id));
          onSuccess?.(field);
          fetchUserFields?.();
        })
        .catch((error) => {
          const { response } = error || {};
          if (response?.status === 400) {
            const { coordinates, area } = response?.data || {};
            if (
              coordinates?.find((err) => err.includes("intersect any land"))
            ) {
              toaster.error({
                title: t("FieldsPage.AddFieldSidebar.Error.Creating.Title"),
                message: t("FieldsPage.AddFieldSidebar.Error.Creating.InWater"),
              });
            } else if (
              coordinates?.find((err) => err.includes("not in greece"))
            ) {
              toaster.error({
                title: t("FieldsPage.AddFieldSidebar.Error.Creating.Title"),
                message: t(
                  "FieldsPage.AddFieldSidebar.Error.Creating.NotInGreece"
                ),
              });
            } else if (area?.find((err) => err.includes("too big"))) {
              toaster.error({
                title: t("FieldsPage.AddFieldSidebar.Error.Creating.Title"),
                message: t(
                  "FieldsPage.AddFieldSidebar.Error.Creating.AreaTooBig"
                ),
              });
            } else {
              toaster.error({
                title: t("FieldsPage.AddFieldSidebar.Error.Creating.Title"),
                message: t("FieldsPage.AddFieldSidebar.Error.Creating.Unkown"),
              });
            }
          }
          onError?.(error);
        });
    },
    [fetchUserFields, navigate, t]
  );

  const updateField = useCallback(
    ({ id, patchData, onSuccess, onError }) => {
      api.fields
        .update(id, patchData)
        .then((res) => {
          onSuccess?.(res.data);
          fetchUserFields?.();
        })
        .catch((error) => {
          const { response } = error || {};
          if (response?.status === 400) {
            const { coordinates, area } = response?.data || {};
            if (
              coordinates?.find((err) => err.includes("intersect any land"))
            ) {
              toaster.error({
                title: t("FieldsPage.AddFieldSidebar.Error.Updating.Title"),
                message: t("FieldsPage.AddFieldSidebar.Error.Creating.InWater"),
              });
            } else if (
              coordinates?.find((err) => err.includes("not in greece"))
            ) {
              toaster.error({
                title: t("FieldsPage.AddFieldSidebar.Error.Creating.Title"),
                message: t(
                  "FieldsPage.AddFieldSidebar.Error.Creating.NotInGreece"
                ),
              });
            } else if (area?.find((err) => err.includes("too big"))) {
              toaster.error({
                title: t("FieldsPage.AddFieldSidebar.Error.Creating.Title"),
                message: t(
                  "FieldsPage.AddFieldSidebar.Error.Creating.AreaTooBig"
                ),
              });
            } else {
              toaster.error({
                title: t("FieldsPage.AddFieldSidebar.Error.Creating.Title"),
                message: t("FieldsPage.AddFieldSidebar.Error.Creating.Unkown"),
              });
            }
          }
          onError?.(error);
        });
    },
    [t, fetchUserFields]
  );

  const deleteField = useCallback(
    ({ id, onSuccess, onError }) => {
      api.fields
        .delete(id)
        .then((res) => {
          onSuccess?.(res.data);
          fetchUserFields?.();
        })
        .catch((err) => onError?.(err));
    },
    [fetchUserFields]
  );

  const cancelCollaboration = useCallback(
    ({ id, onSuccess, onError }) => {
      api.fields
        .removeMe(id)
        .then((res) => {
          onSuccess?.(res.data);
          fetchUserFields?.();
        })
        .catch((err) => onError?.(err));
    },
    [fetchUserFields]
  );

  const value = useMemo(
    () => ({
      fields,
      fetchState,
    }),
    [fields, fetchState]
  );

  const actions = useMemo(
    () => ({
      fetchUserFields,
      createNewField,
      getMyCrops,
      getMyLocations,
      getMyEcoSchemes,
      updateField,
      deleteField,
      cancelCollaboration,
    }),
    [
      fetchUserFields,
      createNewField,
      getMyCrops,
      getMyLocations,
      getMyEcoSchemes,
      updateField,
      deleteField,
      cancelCollaboration,
    ]
  );

  return (
    <FieldsContextActions.Provider value={actions}>
      <FieldsContext.Provider value={value}>{children}</FieldsContext.Provider>
    </FieldsContextActions.Provider>
  );
};

const useFieldsContext = () => {
  const context = useContext(FieldsContext);

  if (!context) {
    throw new Error("useFieldsContext must be used within a FieldsProvider");
  }
  return useContext(FieldsContext);
};

const useFieldsContextActions = () => {
  const context = useContext(FieldsContextActions);

  if (!context) {
    throw new Error(
      "useFieldsContextActions must be used within a FieldsProvider"
    );
  }
  return useContext(FieldsContextActions);
};

export { useFieldsContext, useFieldsContextActions, FieldsProvider };
